import BigNumber from 'bignumber.js';
import {CBSFee} from '../../../../../../../shared/model/cbs-fee';

const LOAN_PAYMENT = 'LOAN_PAYMENT';

// Release and formula fees are not waived here. Pretermination fees are taken from the calculated termination
// fees instead, and loan payment fees are listed combined per fee definition.
const NOT_LISTED_INDIVIDUALLY = ['LOAN_PRETERMINATION', 'LOAN_RELEASE', LOAN_PAYMENT, 'FORMULA'];

const isUnpaid = (fee: CBSFee): boolean => !fee.appliedDate;

const sum = (amounts: number[]): number =>
  amounts.reduce((total, amount) => total.plus(amount || 0), new BigNumber(0)).toNumber();

/**
 * A fee applied on loan payment has its own instance on every amortization line it is charged on, all sharing one
 * fee definition. A waive targets the fee definition and is spread over its unpaid instances, so those are
 * combined into a single fee carrying their total balance.
 */
const combineLoanPaymentFees = (loanFees: CBSFee[]): CBSFee[] => {
  const unpaidInstancesByDefinition = new Map<number, CBSFee[]>();
  loanFees
    .filter(fee => fee.applyOn === LOAN_PAYMENT && isUnpaid(fee))
    .forEach(fee => {
      const instances = unpaidInstancesByDefinition.get(fee.feeDefinitionId);
      if (instances) {
        instances.push(fee);
      } else {
        unpaidInstancesByDefinition.set(fee.feeDefinitionId, [fee]);
      }
    });

  return Array.from(unpaidInstancesByDefinition.values()).map(instances => ({
    ...instances[0],
    amount: sum(instances.map(fee => fee.amount)),
    balance: sum(instances.map(fee => fee.balance))
  }));
};

/**
 * Custom fees of a consolidated loan that can be waived: its other unpaid loan fees (such as amortized fees), its
 * fees applied on loan payment (one entry per fee definition) and the pretermination fees calculated for it.
 */
export const getWaivableCustomFees = (loanFees: CBSFee[], preterminationFees: CBSFee[]): CBSFee[] => {
  const unpaidFees = loanFees.filter(fee => !NOT_LISTED_INDIVIDUALLY.includes(fee.applyOn) && isUnpaid(fee));
  return [...unpaidFees, ...combineLoanPaymentFees(loanFees), ...preterminationFees];
};
